/**
 * BE↔FE shape adapters. Added for the FE↔BE integration.
 *
 * The backend is the canonical source of truth, but its field names and enums
 * differ from the FE's existing domain types. Rather than rewrite the FE's
 * components/reducer, we translate at this single boundary: BE response → FE
 * domain type on the way in, FE value → BE value on the way out.
 *
 * Keeping every mismatch here (and nowhere else) is what lets the rest of the
 * FE stay unchanged.
 */
import type { AdapterClass, ChangeDelivery, Integration, ProductBinding } from '../domain/types';

// ---- Backend wire shapes (only the fields we consume) ----

export interface BeIntegration {
  integration_id: string;
  slug: string;
  display_name: string;
  company: string;
  category: string;
  adapter_class: string; // mcp | openapi | custom
  change_delivery: string; // none | webhook | poll
  allowed_auth_methods: string[];
  recommended_auth_method: string | null;
  brand_color: string | null;
  brand_initials: string | null;
  purpose: string | null;
  is_active: boolean;
}

export interface BeVersion {
  version_id: string;
  vendor_version: string;
  status: string; // draft | published | deprecated
  tag: string; // live | n_1 | none
  rev: number;
  exec_count: number;
}

// ---- Enum normalization (BE → FE) ----

// FE's AdapterClass and BE's adapter_class share the same three values, so this
// is an identity cast guarded to a known set (defensive against future BE values).
export function toAdapterClass(be: string): AdapterClass {
  return be === 'mcp' || be === 'openapi' || be === 'custom' ? be : 'custom';
}

// FE ChangeDelivery is only 'webhook' | 'poll'; BE adds 'none'. The FE card has
// no representation for 'none', so we display it as 'poll' (the most neutral
// "pull-style / no push" option) to avoid breaking the FE type. Documented
// mismatch — see the integration memory.
export function toChangeDelivery(be: string): ChangeDelivery {
  return be === 'webhook' ? 'webhook' : 'poll';
}

// BE version tag 'n_1' ↔ FE display 'N-1'. 'live'/'none' have no FE tag chip.
export function toFeVersionTag(beTag: string): string | null {
  if (beTag === 'n_1') return 'N-1';
  return null; // 'live' and 'none' render no chip in the FE
}

// ---- Grant model mapping (FE product binding → BE canonical) ----
//
// The BE grant model requires product_key = a catalog entity's owner_product
// and data_type = that canonical entity. Catalog v1 only models the
// receptionist slice. The FE uses its own product/dataType vocabulary, so we map:
//
//   FE dataType 'scheduling' → { product_key: 'cal', data_type: 'Engagement' }
//   FE dataType 'contacts'   → { product_key: 'crm', data_type: 'Party'      }
//
// The FE product label (Calendar/CRM/Hire/Tickets/People) is DISPLAY ONLY — we
// send the entity's true owner code underneath so the BE's owner-check passes
// (e.g. "Hire:scheduling" sends product_key 'cal'). FE dataTypes that have no
// canonical entity in v1 (messaging/payments/tickets) return null → the grant
// drawer shows them disabled rather than sending an invalid request.
export interface CanonicalRoute {
  productKey: string; // BE product_key (canonical owner code)
  dataType: string; // BE data_type (canonical entity key)
}

const DATATYPE_TO_CANONICAL: Record<string, CanonicalRoute> = {
  scheduling: { productKey: 'cal', dataType: 'Engagement' },
  contacts: { productKey: 'crm', dataType: 'Party' },
  // messaging / payments / tickets intentionally absent — no canonical entity
  // in catalog v1. mapBinding() returns null for these.
};

/**
 * Map an FE product binding to its canonical BE route, or null if catalog v1
 * cannot represent it. Null is the signal for the grant drawer to render the
 * row disabled with a "not in catalog v1" note.
 */
export function mapBinding(b: ProductBinding): CanonicalRoute | null {
  return DATATYPE_TO_CANONICAL[b.dataType] || null;
}

// ---- Integration mapping (BE → FE) ----

/**
 * Translate a BE integration into the FE's Integration shape. `products` is the
 * FE's binding list; the BE stores eligibility separately (by owner code), so
 * the directory passes the FE seed bindings through unchanged — eligibility is
 * resolved per-binding via mapBinding when grants are toggled.
 */
export function toFeIntegration(be: BeIntegration, products: ProductBinding[]): Integration {
  return {
    id: be.slug, // FE keys connectors by slug (matches FE seed ids)
    name: be.display_name,
    purpose: be.purpose || '',
    cls: toAdapterClass(be.adapter_class),
    color: be.brand_color || '#64748b', // neutral slate fallback if unset
    initials: be.brand_initials || be.display_name.slice(0, 2).toUpperCase(),
    versions: [], // filled by the version list when needed; directory card doesn't require it
    change: toChangeDelivery(be.change_delivery),
    category: be.category,
    company: be.company,
    products,
  };
}
